import SortableTable, {
  RowData,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components/organisms/SortableTable';
import {
  Button,
  CardComponent,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React, { useState } from 'react';
import GifWithLabels from '@HomaHealth/ui-component-library/lib/_patient-access-components/molecules/GIFWithLabels';
import AddParameter from '../AddParameter';
import NoParameters from '../../../../public/Images/NoParameters.png';
import newComparisonMessages from '../../../core-utils/messages/NewComparisonConstants';
import AutocompleteWithCheckBoxes from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Autocomplete';

export interface ParametersSelectionProps {
  vendorType: string;
  selectedParameters: any[];
  setSelectedParameters: React.Dispatch<React.SetStateAction<any[]>>;
  parametersOptions: any[];
}

export const formatToRowData = (parameters: any[]): RowData[] => {
  return parameters.map((param) => ({
    id: param.id,
    name: param.clause,
    value: '',
    type: 'default',
    category: param.category,
    subrows:
      param.provisions?.map((sub: any, subIndex: any) => ({
        id: subIndex + 1,
        name: sub.provision_name,
        value: sub.assessment_description,
        key: sub.key,
        type: sub.type ?? sub.response_type,
        favorability: sub.favorability,
      })) || [],
  }));
};

const useStyles = makeStyles()(() => ({
  hr: {
    width: '100%',
    border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    margin: '18px 0px',
  },
  parametersGrid: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  noParametersGrid: {
    height: '312px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export const ParametersSelection: React.FC<ParametersSelectionProps> = ({
  selectedParameters,
  setSelectedParameters,
  parametersOptions,
}) => {
  //Constant
  const { title, subTitle } = newComparisonMessages.cardTitles.card2;
  const { classes } = useStyles();

  //States
  //Parameters
  const [isOpenAddParameter, setIsOpenAddParameter] = useState<boolean>(false);
  const [selectedParameter, setSelectedParameter] = useState<{
    id: string;
    name: string;
  }>({
    id: '',
    name: '',
  });
  const [parameterEditMode, setParameterEditMode] = useState<boolean>(false);

  //Sub Parameters
  const [isOpenSubParameter, setIsOpenSubParameter] = useState<boolean>(false);
  const [selectedSubParameter, setSelectedSubParameter] = useState<{
    id: string;
    name: string;
    value: string;
    key: string;
    type: string;
    favorability?: string;
  }>({
    id: '',
    name: '',
    value: '',
    key: '',
    favorability: '',
    type: '',
  });
  const [subParameterEditMode, setSubParameterEditMode] = useState<boolean>(
    false,
  );

  //Functions
  const handleAddParameter = () => {
    setParameterEditMode(false);
    setSelectedParameter({ id: '', name: '' });
    setIsOpenAddParameter(true);
  };

  const handleChangeParameter = (
    field: 'name' | 'description' | 'type',
    value: string,
  ) => {
    if (field === 'name') {
      setSelectedParameter((prev) => ({ ...prev, name: value }));
    } else {
      setSelectedParameter((prev) => ({ ...prev, value: value }));
    }
  };

  const handleParameterSave = () => {
    if (parameterEditMode) {
      setSelectedParameters((prev) =>
        prev.map((row) =>
          row.id === selectedParameter.id
            ? {
                ...row,
                name: selectedParameter.name,
                category: 'Additional Category',
              }
            : row,
        ),
      );
    } else {
      setSelectedParameters((prev) => [
        ...prev,
        {
          id: parametersOptions?.length + 1,
          name: selectedParameter.name,
          category: 'Additional Category',
          subrows: [],
        },
      ]);
    }
    setIsOpenAddParameter(false);
  };

  const handleCloseAddParameter = () => {
    setSelectedParameter({ id: '', name: '' });
    setIsOpenAddParameter(false);
  };

  const handleDeleteParameter = (row: any) => {
    setSelectedParameters((prev) =>
      prev.filter((param) => param.id !== row.id),
    );
  };

  const handleEditParameter = (row: any) => {
    setParameterEditMode(true);
    setSelectedParameter({ id: row.id, name: row.name });
    setIsOpenAddParameter(true);
  };

  const handleAddSubParameter = (parentId: any) => {
    setSelectedParameter({ id: parentId, name: '' });
    setSelectedSubParameter({ id: '', name: '', value: '', key: '', type: '' });
    setIsOpenSubParameter(true);
  };

  const handleChangeSubParameter = (
    field: 'name' | 'description' | 'type',
    value: string,
  ) => {
    switch (field) {
      case 'name':
        setSelectedSubParameter((prev) => ({ ...prev, name: value }));
        break;
      case 'description':
        setSelectedSubParameter((prev) => ({ ...prev, value }));
        break;
      case 'type':
        setSelectedSubParameter((prev) => ({ ...prev, type: value }));
        break;
      default:
    }
  };

  const handleSaveSubParameter = () => {
    if (!subParameterEditMode) {
      setSelectedParameters((prev) =>
        prev.map((row) =>
          row.id === selectedParameter?.id
            ? {
                ...row,
                subrows: [
                  ...(row.subrows || []),
                  {
                    id: row.subrows?.length + 1,
                    name: selectedSubParameter.name,
                    value: selectedSubParameter.value,
                    type: selectedSubParameter.type,
                  },
                ],
              }
            : row,
        ),
      );
    } else {
      setSelectedParameters((prev) =>
        prev.map((row) =>
          row.id === selectedParameter?.id
            ? {
                ...row,
                 
                subrows: row.subrows?.map((sub: any) =>
                  sub.id === selectedSubParameter?.id
                    ? {
                        ...sub,
                        name: selectedSubParameter.name,
                        value: selectedSubParameter.value,
                        type: selectedSubParameter.type,
                      }
                    : sub,
                ),
              }
            : row,
        ),
      );
    }

    // Reset and close modal
    setIsOpenSubParameter(false);
  };

  const handleCloseSubParameter = () => {
    setSelectedSubParameter({ id: '', name: '', value: '', key: '', type: '' });
    setIsOpenSubParameter(false);
  };

  const handleEditSubParameter = (subRow: any, parentId: any) => {
    setSubParameterEditMode(true);
    setSelectedParameter({ id: parentId, name: '' });
    setSelectedSubParameter({
      id: subRow.id,
      name: subRow.name,
      value: subRow.value,
      key: subRow.key,
      type: subRow.type,
    });
    setIsOpenSubParameter(true);
  };

  const handleDeleteSubParameter = (subRowId: any, parentId: any) => {
    setSelectedParameters((prev) =>
      prev.map((row) =>
        row.id === parentId
          ? {
              ...row,
               
              subrows: row.subrows?.filter((sub: any) => sub.id !== subRowId),
            }
          : row,
      ),
    );
  };

  return (
    <>
      <CardComponent
        title={title}
        subTitle={subTitle}
        mainGridStyles={{
          marginTop: '10px',
        }}
        body={
          <>
            <hr className={classes.hr} />
            <Grid className={classes.parametersGrid}>
              <Grid>
                <Typography
                  fontType="medium"
                  fontSize="12px"
                  textColor={patientAccessTheme.palette.text.mediumemphasis}
                  margin="0px 0px 8px 0px"
                >
                  Parameters
                </Typography>
                <AutocompleteWithCheckBoxes
                  placeholder={'Select Parameter'}
                  width="320px"
                  options={parametersOptions?.map((item) => item?.clause)}
                  defaultValue={selectedParameters
                    ?.filter((item) => item?.type === 'default')
                    ?.map((item) => item?.name)}
                  handleChange={(event, selectedNames, reason, details) => {
                    if (reason === 'select-option') {
                      const selectedValue = details?.option;
                      const selectedObjects = parametersOptions?.filter(
                        (param: any) => param.clause === selectedValue,
                      );
                      const formattedRows = formatToRowData(selectedObjects);
                      setSelectedParameters((prev) => [
                        ...prev,
                        ...formattedRows,
                      ]);
                    }
                    if (reason === 'remove-option' || reason === 'clear') {
                      const selectedValue = details?.option;
                      setSelectedParameters((prev) =>
                        prev.filter((param) => param.name !== selectedValue),
                      );
                    }
                  }}
                />
              </Grid>
              <Button
                label={'Add Parameter'}
                variant="contained"
                width="auto"
                onClick={handleAddParameter}
              />
            </Grid>
            <SortableTable
              columns={[
                { name: 'Parameters', width: '30%' },
                { name: 'Description', width: '60%' },
                { name: '', width: '' },
              ]}
              rows={selectedParameters}
              setRows={setSelectedParameters}
              handleAddSubParameter={(parentId) => {
                handleAddSubParameter(parentId);
              }}
              handleEdit={(subRow, parentId) => {
                handleEditSubParameter(subRow, parentId);
              }}
              handleDelete={(subRowId, parentId) => {
                handleDeleteSubParameter(subRowId, parentId);
              }}
              tableWidth="100%"
              handleDeleteParameter={handleDeleteParameter}
              handleEditParameter={handleEditParameter}
            />
            {selectedParameters?.length === 0 && (
              <Grid className={classes.noParametersGrid}>
                <GifWithLabels
                  mainLabel={
                    <Typography
                      fontSize="14px"
                      fontType="medium"
                      textColor={patientAccessTheme.palette.text.highemphasis}
                      margin="16px 0px 0px 0px"
                    >
                      Select parameters to see the list
                    </Typography>
                  }
                  subLabel={undefined}
                  gifUrl={NoParameters}
                />
              </Grid>
            )}
          </>
        }
      />
      {isOpenAddParameter && (
        <AddParameter
          name={selectedParameter.name}
          onChange={handleChangeParameter}
          onSave={handleParameterSave}
          onClose={handleCloseAddParameter}
          title={parameterEditMode ? 'Edit Parameter' : 'Add Parameter'}
        />
      )}
      {isOpenSubParameter && (
        <AddParameter
          name={selectedSubParameter.name}
          description={selectedSubParameter.value}
          onChange={handleChangeSubParameter}
          onSave={handleSaveSubParameter}
          onClose={handleCloseSubParameter}
          title={
            subParameterEditMode ? 'Edit Sub-Parameter' : 'Add Sub-Parameter'
          }
          responseType={selectedSubParameter.type}
        />
      )}
    </>
  );
};
